import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Check, X, ArrowLeft, Sparkles } from "lucide-react";

/**
 * Tracks a multi-product Try-on batch — polls every 4s until status is 'done'.
 * Shows per-product progress with preview thumbnails.
 */
export default function AdminTryOnBatchDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [savingProductId, setSavingProductId] = useState(null);
  const [picked, setPicked] = useState({});  // {productId: {url: true}}

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/admin/try-on/batches/${id}`);
        if (cancelled) return;
        setData(data);
        if (data?.batch?.status === "running") {
          setTimeout(poll, 4000);
        }
      } catch (e) {
        if (cancelled) return;
        toast.error("Couldn't load batch");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [id]);

  const togglePick = (productId, url) => {
    setPicked((p) => ({
      ...p,
      [productId]: { ...(p[productId] || {}), [url]: !((p[productId] || {})[url]) },
    }));
  };

  const saveProduct = async (productId) => {
    const urls = Object.entries(picked[productId] || {}).filter(([, v]) => v).map(([k]) => k);
    if (urls.length === 0) return toast.error("Tick at least one image first");
    setSavingProductId(productId);
    try {
      const { data } = await api.post("/admin/try-on/save", {
        product_id: productId,
        image_urls: urls,
      });
      toast.success(`Added ${data.added} image(s) to product`);
      setPicked((p) => ({ ...p, [productId]: {} }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingProductId(null);
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-[hsl(var(--kb-muted))]">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading…
      </div>
    );
  }

  const { batch, jobs } = data;
  const pct = batch.product_count
    ? Math.round((batch.completed_count / batch.product_count) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="try-on-batch-detail">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Link to="/admin/products" className="text-xs text-[hsl(var(--kb-muted))] inline-flex items-center gap-1 hover:text-[hsl(var(--kb-primary))]">
            <ArrowLeft size={12} /> Back to products
          </Link>
          <h1 className="font-display text-3xl mt-1 flex items-center gap-2">
            <Sparkles size={22} /> Try-on Batch
          </h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
            Style: <span className="font-medium uppercase">{batch.style}</span> ·
            {" "}{batch.product_count} product(s) · created {new Date(batch.created_at).toLocaleString()}
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-[hsl(var(--kb-muted))] uppercase tracking-wider">Status</div>
          <div className={`text-lg font-display ${batch.status === "done" ? "text-green-700" : ""}`}>
            {batch.status === "running"
              ? `${batch.completed_count}/${batch.product_count} done`
              : "Complete"}
          </div>
        </div>
      </div>

      <div className="bg-[hsl(var(--kb-surface-alt))] h-2 w-full" data-testid="batch-progress-bar">
        <div className="h-2 bg-[hsl(var(--kb-primary))] transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="space-y-4" data-testid="batch-job-list">
        {jobs.map((j) => {
          const product = picked[j.product_id] || {};
          return (
            <section key={j.id} className="bg-white border border-[hsl(var(--kb-border))]" data-testid={`batch-job-${j.id}`}>
              <div className="px-5 py-3 border-b border-[hsl(var(--kb-border))] flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">{j.product_title || j.product_id}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))]">
                    {j.status === "running" && (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 size={10} className="animate-spin" /> Generating {(j.previews?.length || 0) + (j.failed_angles?.length || 0)} / {j.total_angles}
                      </span>
                    )}
                    {j.status === "done" && `${j.previews?.length || 0} previews · ${j.failed_angles?.length || 0} failed`}
                    {j.status === "failed" && <span className="text-red-700">Failed: {j.error}</span>}
                  </div>
                </div>
                {j.status === "done" && (j.previews?.length || 0) > 0 && (
                  <button
                    type="button"
                    onClick={() => saveProduct(j.product_id)}
                    disabled={savingProductId === j.product_id}
                    className="kb-btn-primary !py-2 !px-3 !text-xs inline-flex items-center gap-1"
                    data-testid={`save-${j.product_id}`}
                  >
                    {savingProductId === j.product_id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                    Save selected
                  </button>
                )}
              </div>
              {j.previews?.length > 0 && (
                <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {j.previews.map((p) => {
                    const active = !!product[p.url];
                    const display = p.url.startsWith("http") ? p.url : `${process.env.REACT_APP_BACKEND_URL}${p.url}`;
                    return (
                      <button
                        key={p.url}
                        type="button"
                        onClick={() => togglePick(j.product_id, p.url)}
                        className={`relative aspect-[4/5] border-2 transition ${active ? "border-[hsl(var(--kb-primary))]" : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/50"}`}
                      >
                        <img src={display} alt={p.angle} className="w-full h-full object-cover" />
                        <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] uppercase tracking-wider px-1 py-0.5">
                          {p.angle.replace(/_/g, " ")}
                        </span>
                        <span className={`absolute top-1 right-1 rounded-full w-5 h-5 flex items-center justify-center ${active ? "bg-[hsl(var(--kb-primary))] text-white" : "bg-white/80 text-[hsl(var(--kb-muted))]"}`}>
                          {active ? <Check size={12} /> : <X size={12} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
